import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { formatApiError } from "../lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[85vh] flex items-center justify-center px-4 py-12">
      <div className="aurora opacity-50" />
      <div className="relative w-full max-w-md z-10">
        <div className="surface-card p-7 sm:p-10 rise">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="login-title">
            {t("auth_login_title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1.5">{t("brand")}</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <label className="input-label">{t("auth_email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="input-label">{t("auth_password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3.5 py-3 text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="btn btn-primary btn-lg w-full disabled:opacity-60"
            >
              {loading ? t("common_loading") : t("auth_login_btn")}
              {!loading && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
          </form>

          <p className="mt-7 text-sm text-center text-zinc-500">
            {t("auth_no_account")}{" "}
            <Link
              to="/register"
              data-testid="login-register-link"
              className="font-semibold text-zinc-900 hover:underline underline-offset-4"
            >
              {t("auth_register_link")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
